import styled, { css } from 'styled-components';

import { colors, radius } from '../../../../styles/tokens';

export const Container = styled.div`
  padding: 40px;
  min-height: 100%;

  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (max-width: 1199px) {
    padding: 20px;
  }
`;

export const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
`;

export const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 800;
  color: ${colors.onSurface};
  letter-spacing: -0.01em;
  margin-bottom: 8px;
`;

export const PageSubtitle = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: ${colors.onSurfaceVariant};
`;

export const NotifyButton = styled.button`
  padding: 8px 16px;
  border-radius: ${radius.DEFAULT};
  border: none;
  background-color: ${colors.primary};

  font-size: 12px;
  font-weight: 700;
  color: ${colors.onPrimary};
  cursor: pointer;
  white-space: nowrap;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

export const LockedState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 80px 24px;
  text-align: center;
  color: ${colors.onSurfaceVariant};

  h2 {
    color: ${colors.onSurface};
    font-size: 18px;
    font-weight: 800;
  }
`;

export const FiltersRow = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
`;

export const SelectWrapper = styled.div`
  min-width: 220px;
`;

export const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;

  font-size: 13px;
  font-weight: 600;
  color: ${colors.onSurfaceVariant};

  input {
    width: 16px;
    height: 16px;
    accent-color: ${colors.primary};
    cursor: pointer;
  }
`;

export const BulkActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  padding: 12px 16px;
  border-radius: ${radius.DEFAULT};
  background-color: ${colors.surfaceContainerLow};

  span {
    font-size: 12px;
    font-weight: 700;
    color: ${colors.onSurfaceVariant};
    margin-right: auto;
  }
`;

export const BulkButton = styled.button`
  padding: 8px 14px;
  border-radius: ${radius.DEFAULT};
  border: 1px solid ${colors.outlineVariant};
  background: transparent;
  color: ${colors.onSurface};

  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: ${colors.primary};
    color: ${colors.primary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

interface RowProps {
  tone: 'danger' | 'warning' | 'paid' | 'none';
}

export const Row = styled.tr<RowProps>`
  ${({ tone }) =>
    tone === 'danger' &&
    css`
      background-color: ${colors.error}14;
    `}

  ${({ tone }) =>
    tone === 'warning' &&
    css`
      background-color: ${colors.primary}14;
    `}

  ${({ tone }) =>
    tone === 'paid' &&
    css`
      opacity: 0.62;
    `}
`;

export const StudentInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const StudentAvatar = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

export const StudentName = styled.span<{ paid: boolean }>`
  font-size: 14px;
  font-weight: 700;
  color: ${colors.onSurface};

  ${({ paid }) =>
    paid &&
    css`
      text-decoration: line-through;
      text-decoration-color: ${colors.primary}80;
    `}
`;

interface StatusBadgeProps {
  tone: RowProps['tone'];
}

const badgeTone = {
  danger: { bg: `${colors.error}1a`, color: colors.error },
  warning: { bg: `${colors.primary}1a`, color: colors.primary },
  paid: { bg: colors.surfaceContainerHighest, color: colors.onSurfaceVariant },
  none: { bg: colors.surfaceContainerHighest, color: colors.onSurfaceVariant },
};

export const StatusBadge = styled.span<StatusBadgeProps>`
  display: inline-flex;
  padding: 6px 14px;
  border-radius: ${radius.full};

  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;

  background-color: ${({ tone }) => badgeTone[tone].bg};
  color: ${({ tone }) => badgeTone[tone].color};
`;

export const MarkPaidButton = styled.button`
  padding: 8px 14px;
  border-radius: ${radius.DEFAULT};
  border: 1px solid ${colors.outlineVariant};
  background: transparent;
  color: ${colors.onSurface};

  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: #32a54a;
    color: #32a54a;
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

export const PaidLabel = styled.span`
  font-size: 12px;
  color: ${colors.onSurfaceVariant};
`;
